
import Vue from 'vue';
import _trim from 'lodash/trim';
import { db, FieldValue } from '@/firebase';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import { mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'SnapshotSessionDialog',
	props: {
		value: { type: Boolean, default: false },
		session: { type: Object, required: true },
	},
	data () {
		return {
			dialogOpen: false,
			submitting: false,
			submitted: false,
			name: '',
			note: '',
		};
	},
	computed: {
		snapshotRequest (): any {
			return (this.session && this.session.snapshotRequest) || null;
		},
		status (): string {
			return (this.snapshotRequest && this.snapshotRequest.status) || '';
		},
		statusMessage (): string {
			return (this.snapshotRequest && this.snapshotRequest.statusMessage) || '';
		},
		errorMessage (): string {
			return (this.snapshotRequest && this.snapshotRequest.errorMessage) || '';
		},
		isRunning (): boolean {
			return !this.status || this.status === 'pending' || this.status === 'running';
		},
		isSucceeded (): boolean {
			return this.status === 'succeeded';
		},
		isFailed (): boolean {
			return this.status === 'failed';
		},
		fieldsDisabled (): boolean {
			return this.submitting;
		},
		confirmDisabled (): boolean {
			if (this.submitted) return this.isRunning;
			return !_trim(this.name);
		},
		title (): string {
			if (!this.submitted) return 'Snapshot Session';
			if (this.isSucceeded) return 'Snapshot Ready';
			if (this.isFailed) return 'Snapshot Failed';
			return 'Taking Snapshot…';
		},
		confirmText (): string {
			if (!this.submitted) return 'Take Snapshot';
			return 'Close';
		},
		statusHeading (): string {
			if (this.isSucceeded) return 'Snapshot complete';
			if (this.isFailed) return 'Snapshot failed';
			if (this.status === 'running') return 'Running';
			return 'Queued';
		},
	},
	watch: {
		value (newVal: boolean) {
			this.dialogOpen = !!newVal;
			if (newVal) {
				this.name = '';
				this.note = '';
				this.submitted = false;
			}
		},
		dialogOpen (newVal: boolean) {
			if (newVal !== this.value) this.$emit('input', newVal);
		},
	},
	methods: {
		...mapActions(['updateSnackbar']),
		async onConfirm (closeDialog: () => void) {
			if (this.submitted) {
				closeDialog();
				return;
			}
			const trimmedName = _trim(this.name);
			if (!trimmedName) return;
			// Preconditions mirror the orchestrator's snapshotRequest contract.
			// The Snapshot button is already gated on these in SessionBar.vue, but re-check
			// here so a late state change still surfaces a friendly error.
			const session = this.session;
			if (session.region !== 'MOB-1' && session.region !== 'MOB-DEV-1') {
				this.showError('Snapshots are only supported on Mobius sessions.');
				return;
			}
			if (session.state !== 'READY') {
				this.showError(`Session must be READY to snapshot (current: ${session.state}).`);
				return;
			}
			const deploymentImage: string = session.deploymentImage || '';
			if (!deploymentImage.startsWith('comfyui')) {
				this.showError('Snapshots are only supported for ComfyUI today.');
				return;
			}
			if (session.snapshotRequest && session.snapshotRequest.status === 'running') {
				this.showError('A snapshot is already in progress for this session.');
				return;
			}
			this.submitting = true;
			try {
				// Replace any prior snapshotRequest so stale orchestrator fields (status, resultTag, etc.)
				// don't leak into the new request — the orchestrator owns those keys.
				await db.doc(`sessions/${session.id}`).update({
					snapshotRequest: {
						name: trimmedName,
						note: _trim(this.note),
						requestedAt: FieldValue.serverTimestamp(),
					},
				});
				this.submitted = true;
			} catch (e: any) {
				this.showError(e?.message || 'Failed to request snapshot.');
			} finally {
				this.submitting = false;
			}
		},
		showError (message: string) {
			this.updateSnackbar({
				status: SNACKBAR_STATUS.ERROR,
				message,
				show: true,
				timeout: 6000,
			});
		},
	},
	components: {
		ConfirmDialog,
	},
});
